import { atom, useAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { reclaim } from "../../reclaim-api";
import { ConnectedAccount } from "../../reclaim-api/Accounts";
import { CalendarView } from "../../reclaim-api/Calendars";
import { useNotifications } from "../useNotifications";
import { useOurRouter } from "../useOurRouter";

const accountsAtom = atom<ConnectedAccount[] | undefined>(undefined);
const setAccountsAtom = atom(null, (get, set, accounts: ConnectedAccount[] | undefined) => set(accountsAtom, accounts));

const initAtom = atom<boolean>(false);
const setInitAtom = atom(null, (get, set, initialized: boolean) => set(initAtom, initialized));

const loadingAtom = atom<boolean>(false);
const setLoadingAtom = atom(null, (get, set, loading: boolean) => set(loadingAtom, loading));

const loadingErrorAtom = atom<boolean>(false);
const setLoadingErrorAtom = atom(null, (get, set, loadingError: boolean) => set(loadingErrorAtom, loadingError));

export type UseAccountsActionsReturnType = {
  load: () => Promise<ConnectedAccount[] | undefined>;
  setNewMainAccount: (account: ConnectedAccount) => Promise<void>; // triggers redirect
  deleteAccount: (accountId: number) => Promise<void>;
};

export const useAccountsActions = (): UseAccountsActionsReturnType => {
  const [, setAccounts] = useAtom(setAccountsAtom);
  const [, setInit] = useAtom(setInitAtom);
  const [, setLoading] = useAtom(setLoadingAtom);
  const [, setLoadingError] = useAtom(setLoadingErrorAtom);

  const router = useOurRouter();
  const { sendNotification } = useNotifications();

  const load = useCallback(async () => {
    let accounts: ConnectedAccount[] | undefined = undefined;

    void setLoading(true);
    try {
      accounts = await reclaim.accounts.list();
      void setLoadingError(false);
    } catch (cause) {
      void setLoadingError(true);
    } finally {
      void setLoading(false);
    }

    if (!!accounts) {
      await setAccounts(accounts);
      await setInit(true);
      return accounts;
    }
  }, [setAccounts, setInit, setLoading, setLoadingError]);

  const setNewMainAccount = useCallback(
    async (account: ConnectedAccount) => {
      if (!account.switchToMainURI) {
        sendNotification({
          message: "Cannot set account to main. Please contact support if this issue persists.",
          type: "error",
        });
        return;
      }

      await reclaim.accounts.authRedirect(account.switchToMainURI, {
        redirect: router.asPath,
        accountId: account.id,
      });
    },
    [router.asPath, sendNotification]
  );

  const deleteAccount = useCallback(
    async (accountId: number) => {
      try {
        await reclaim.accounts.delete(accountId);
        await load();
        sendNotification({ message: "Account deleted." });
      } catch (cause) {
        sendNotification({
          message: "Could not delete account. Please contact support if this problem persists.",
          type: "error",
        });
      }
    },
    [load, sendNotification]
  );

  const actions = useMemo<UseAccountsActionsReturnType>(
    () => ({ load, setNewMainAccount, deleteAccount }),
    [load, setNewMainAccount, deleteAccount]
  );

  return actions;
};

export type UseAccountsStateReturnType = {
  accounts?: ConnectedAccount[];
  initialized: boolean;
  loading: boolean;
  loadingError: boolean;
  connectedCalendars: CalendarView<true>[];
  mainAccount: ConnectedAccount | undefined;
};

export const useAccountsState = (): UseAccountsStateReturnType => {
  const [accounts] = useAtom(accountsAtom);
  const [initialized] = useAtom(initAtom);
  const [loading] = useAtom(loadingAtom);
  const [loadingError] = useAtom(loadingErrorAtom);

  const connectedCalendars = useMemo<CalendarView<true>[]>(() => {
    return accounts?.reduce((p, c) => [...p, ...(c.connectedCalendars || [])], []) || [];
  }, [accounts]);

  const mainAccount = useMemo(() => accounts?.find(({ main }) => main), [accounts]);

  const state = useMemo<UseAccountsStateReturnType>(
    () => ({ accounts, initialized, loading, loadingError, connectedCalendars, mainAccount }),
    [accounts, initialized, loading, loadingError, connectedCalendars, mainAccount]
  );

  return state;
};
